import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from 'app/contexts/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Constants
const API_BASE_URL = process.env.REACT_APP_SERVER_IP;
const API_USER_MNGMNTS = process.env.REACT_APP_USER_MANAGEMENT;

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        "Content-Type": "application/json",
    },
});

// Password validation function
const validatePassword = (password, t) => {
    if (password.length < 8) {
        return t("Password must be at least 8 characters long");
    }
    if (!/[A-Z]/.test(password)) {
        return t("Password must contain at least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
        return t("Password must contain at least one lowercase letter");
    }
    if (!/[0-9]/.test(password)) {
        return t("Password must contain at least one number");
    }
    if (!/[^A-Za-z0-9]/.test(password)) {
        return t("Password must contain at least one special character");
    }
    return null;
};

const UnifiedLogin = ({ isEmployeeLogin = true, isResetPassword = false }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        newPassword: '',
        oldPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState({
        password: false,
        newPassword: false,
        oldPassword: false
    });
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const navigate = useNavigate();
    const { isAuthenticated, setIsAuthenticated, setIsLoading, isLoading } = useAuthContext();
    const location = useLocation();

    // Set email from URL params if coming from password change requirement
    useEffect(() => {
        if (location.search) {
            const params = new URLSearchParams(location.search);
            const emailParam = params.get('email');
            if (emailParam) {
                setFormData(prev => ({ ...prev, email: decodeURIComponent(emailParam) }));
            }
        }

        if (location.state?.passwordChanged) {
            setSuccessMessage(t('Password changed successfully. Please log in with your new password.'));
        }
    }, [location, t]);

    // Handle redirection if already authenticated
    useEffect(() => {
        if (isAuthenticated) {
            const initialLogin = sessionStorage.getItem("InitialLogin");
            if (initialLogin === "false" && !isEmployeeLogin) {
                navigate("/reset-password");
            } else {
                navigate(isEmployeeLogin ? '/main' : '/request');
            }
        }
    }, [isAuthenticated, navigate, isEmployeeLogin]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear validation error when user types
        if (validationErrors[name]) {
            setValidationErrors(prev => ({ ...prev, [name]: '' }));
        }

        // Clear messages when user types
        if (error) setError('');
        if (successMessage) setSuccessMessage('');
    }, [validationErrors, error, successMessage]);

    const validateResetPassword = useCallback(() => {
        const errors = {};

        if (isEmployeeLogin) {
            if (!formData.oldPassword) {
                errors.oldPassword = t('Current password is required');
            }
        }

        if (!formData.newPassword) {
            errors.newPassword = t('New password is required');
        } else {
            const passwordError = validatePassword(formData.newPassword, t);
            if (passwordError) {
                errors.newPassword = passwordError;
            }
        }

        if (!formData.confirmPassword) {
            errors.confirmPassword = t('Please confirm your password');
        } else if (formData.newPassword !== formData.confirmPassword) {
            errors.confirmPassword = t('Passwords do not match');
        }

        return errors;
    }, [formData, isEmployeeLogin, t]);

    const handleEmployeeLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setValidationErrors({});

        try {
            const response = await fetch(`${API_USER_MNGMNTS}login/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: formData.email,
                    password: formData.password
                })
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 403 && data.message === 'Password change required on first login') {
                    sessionStorage.setItem("accessToken", data?.access_token);
                    if (sessionStorage.getItem('accessToken')) {
                        navigate(`/employee-password-change/?email=${encodeURIComponent(formData.email)}`);
                    }
                    return;
                }
                throw new Error(data.error || data.message || t('Login failed. Please check your credentials.'));
            }

            const { access, refresh, admin_unit, employee_id, org_unit, permissions, roles, username, user_id, full_name } = data.user || data;

            if (!access) {
                throw new Error(t('No access token received'));
            }

            sessionStorage.setItem("accessToken", access);
            sessionStorage.setItem("refreshToken", refresh);
            sessionStorage.setItem("admin_unit", admin_unit);
            sessionStorage.setItem("employee_id", employee_id);
            sessionStorage.setItem("org_unit", org_unit);
            sessionStorage.setItem("permissions", JSON.stringify(permissions));
            sessionStorage.setItem("roles", JSON.stringify(roles));
            sessionStorage.setItem("username", username);
            sessionStorage.setItem("full_name", full_name);
            sessionStorage.setItem("user_id", user_id);
            sessionStorage.setItem("detail", JSON.stringify(data));
            if (response.status === 200) {
                setIsAuthenticated(true);
            }

        } catch (err) {
            setError(err.message || t('An error occurred during login'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClientLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setValidationErrors({});

        try {
            const response = await api.post('/login/', {
                email: formData.email,
                password: formData.password
            });

            const { access_token, refresh_token, initial_login, uu_id, organization } = response.data;

            sessionStorage.setItem("accessToken", access_token);
            sessionStorage.setItem("refreshToken", refresh_token);
            sessionStorage.setItem("InitialLogin", initial_login);
            sessionStorage.setItem("client", uu_id);
            sessionStorage.setItem("organization", organization ? organization.uu_id : null);
            sessionStorage.setItem("detail", JSON.stringify(response.data));

            setIsAuthenticated(true);
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                t('Login failed. Please check your credentials.');
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEmployeePasswordReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        const errors = validateResetPassword();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setIsLoading(false);
            return;
        }

        try {
            const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");

            if (!token) {
                throw new Error(t("Authentication credentials were not provided. Please login again."));
            }

            const response = await fetch(`${API_USER_MNGMNTS}password-change/`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    email: formData.email,
                    old_password: formData.oldPassword,
                    new_password: formData.newPassword
                })
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error(t("Authentication credentials were not provided or are invalid."));
                }
                throw new Error(data.message || data.error || t('Password reset failed. Please try again.'));
            }

            // Show success message and clear form
            setSuccessMessage(t("Password changed successfully!"));
            setFormData(prev => ({
                ...prev,
                oldPassword: '',
                newPassword: '',
                confirmPassword: ''
            }));

            // Optionally redirect after delay
            setTimeout(() => {
                navigate("/login", { state: { passwordChanged: true } });
            }, 2000);

        } catch (error) {
            setError(error.message || t('Password reset failed. Please try again.'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClientPasswordReset = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');
        setSuccessMessage('');

        const errors = validateResetPassword();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            setIsLoading(false);
            return;
        }

        try {
            const token = sessionStorage.getItem("accessToken");
            if (!token) {
                setError(t("Session expired. Please login again."));
                navigate("/");
                return;
            }

            const response = await api.post("/force-password-change/", {
                new_password: formData.newPassword,
                confirm_password: formData.confirmPassword
            }, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.status === 200) {
                setSuccessMessage(t("Password changed successfully!"));
                setFormData(prev => ({
                    ...prev,
                    newPassword: '',
                    confirmPassword: ''
                }));

                // Optionally redirect after delay
                setTimeout(() => {
                    navigate("/login", { state: { passwordChanged: true } });
                }, 2000);
            } else {
                throw new Error(response.data?.error || t('Password reset failed. Please try again.'));
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                t('Password reset failed. Please try again.');
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = isResetPassword
        ? (isEmployeeLogin ? handleEmployeePasswordReset : handleClientPasswordReset)
        : (isEmployeeLogin ? handleEmployeeLogin : handleClientLogin);

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    };

    return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12">
            <div className="w-full max-w-6xl flex flex-col md:flex-row gap-6 sm:gap-8 md:gap-12 lg:gap-16 rounded-lg shadow-lg p-4 sm:p-6">
                {/* Left side - Image/Background */}
                <div
                    className="w-full md:w-1/2 rounded-xl p-6 sm:p-8 lg:p-12 flex flex-col justify-between"
                    style={{
                        background: 'radial-gradient(37.95% 41.6% at 24.33% 20.02%, #212121 0%, #432101 100%)',
                        minHeight: '500px'
                    }}
                >
                    <div>
                        <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                            <span className="text-red-600">{t("Better Oromia,")}</span>
                        </p>
                        <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
                            {t("Better Ethiopia")}
                        </p>
                        <p className="text-xs sm:text-sm md:text-base text-white opacity-80 max-w-md">
                            {t("Lorem ipsum dolor sit amet, consectetur adipiscing elit.")}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 border-r border-gray-200 pr-2">
                        <img
                            src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
                            alt={t("Logo")}
                            className="w-10 sm:w-12 md:w-16 h-10 sm:h-12 object-contain"
                        />
                        <div>
                            <p className="text-white text-sm sm:text-base md:text-lg font-bold">
                                {t("Biiroo Abba Alangaa W.O.")}
                            </p>
                            <p className="text-amber-300 text-xs sm:text-sm md:text-base font-semibold">
                                የኦሮሚያ ጠቅላይ ዐቃቤ ሕግ ቢሮ
                            </p>
                        </div>
                    </div>
                </div>

                {/* Right side - Form */}
                <div className="w-full md:w-1/2 flex items-center justify-center py-6 sm:py-8">
                    <div className="w-full max-w-md px-4 sm:px-0">
                        <div className="mb-6 sm:mb-8">
                            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                                {isResetPassword ? t("Reset Password") : t("Login")}
                            </h1>
                            <p className="text-gray-600 text-xs sm:text-sm md:text-base">
                                {isResetPassword
                                    ? t("Please enter your new password")
                                    : t("Please fill out this form carefully to start your daily task process.")}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                            {!isResetPassword ? (
                                <>
                                    <div>
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder={t("Email Address")}
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:text-base"
                                            required
                                            autoComplete="username"
                                        />
                                    </div>
                                    <div className="relative">
                                        <input
                                            type={showPassword.password ? "text" : "password"}
                                            name="password"
                                            placeholder={t("Password")}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:text-base"
                                            required
                                            autoComplete="current-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('password')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                                            aria-label={showPassword.password ? t("Hide password") : t("Show password")}
                                        >
                                            <FontAwesomeIcon icon={showPassword.password ? faEyeSlash : faEye} />
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    {isEmployeeLogin && (
                                        <>
                                            <div>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    placeholder={t("Email")}
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-2 sm:py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm sm:text-base"
                                                    required
                                                    autoComplete="username"
                                                    readOnly={!!location.search}
                                                />
                                            </div>
                                            <div className="relative">
                                                <input
                                                    type={showPassword.oldPassword ? "text" : "password"}
                                                    name="oldPassword"
                                                    placeholder={t("Current Password")}
                                                    value={formData.oldPassword}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-2 sm:py-3 border rounded-md focus:outline-none focus:ring-2 text-sm sm:text-base ${validationErrors.oldPassword
                                                        ? 'border-red-500 focus:ring-red-500'
                                                        : 'border-gray-300 focus:ring-gray-500'
                                                        }`}
                                                    required
                                                    autoComplete="current-password"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => togglePasswordVisibility('oldPassword')}
                                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                                                    aria-label={showPassword.oldPassword ? t("Hide password") : t("Show password")}
                                                >
                                                    <FontAwesomeIcon icon={showPassword.oldPassword ? faEyeSlash : faEye} />
                                                </button>
                                                {validationErrors.oldPassword && (
                                                    <p className="mt-1 text-xs text-red-500">{validationErrors.oldPassword}</p>
                                                )}
                                            </div>
                                        </>
                                    )}
                                    <div className="relative">
                                        <input
                                            type={showPassword.newPassword ? "text" : "password"}
                                            name="newPassword"
                                            placeholder={t("New Password")}
                                            value={formData.newPassword}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 sm:py-3 border rounded-md focus:outline-none focus:ring-2 text-sm sm:text-base ${validationErrors.newPassword
                                                ? 'border-red-500 focus:ring-red-500'
                                                : 'border-gray-300 focus:ring-gray-500'
                                                }`}
                                            required
                                            autoComplete="new-password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => togglePasswordVisibility('newPassword')}
                                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
                                            aria-label={showPassword.newPassword ? t("Hide password") : t("Show password")}
                                        >
                                            <FontAwesomeIcon icon={showPassword.newPassword ? faEyeSlash : faEye} />
                                        </button>
                                        {validationErrors.newPassword && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.newPassword}</p>
                                        )}
                                    </div>
                                    <div>
                                        <input
                                            type="password"
                                            name="confirmPassword"
                                            placeholder={t("Confirm Password")}
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-2 sm:py-3 border rounded-md focus:outline-none focus:ring-2 text-sm sm:text-base ${validationErrors.confirmPassword
                                                ? 'border-red-500 focus:ring-red-500'
                                                : 'border-gray-300 focus:ring-gray-500'
                                                }`}
                                            required
                                            autoComplete="new-password"
                                        />
                                        {validationErrors.confirmPassword && (
                                            <p className="mt-1 text-xs text-red-500">{validationErrors.confirmPassword}</p>
                                        )}
                                    </div>
                                </>
                            )}

                            {error && (
                                <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
                                    {error}
                                </div>
                            )}

                            {successMessage && (
                                <div className="p-3 bg-green-50 text-green-600 rounded-md text-sm">
                                    {successMessage}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={isLoading}
                                className={`w-full py-2 sm:py-3 px-4 rounded-md font-semibold text-white transition-colors flex items-center justify-center
                                    ${isLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-gray-900 hover:bg-gray-800'}`}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('Processing...')}
                                    </>
                                ) : isResetPassword ? t('Reset Password') : t('Login')}
                            </button>

                            {!isResetPassword && (
                                <div className="text-center text-sm">
                                    <button
                                        type="button"
                                        onClick={() => navigate(isEmployeeLogin ? '/employee-password-change' : '/forgot-password')}
                                        className="font-medium text-gray-600 hover:text-gray-900"
                                    >
                                        {t('Forgot your password?')}
                                    </button>
                                </div>
                            )}
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnifiedLogin;